import { useState, useEffect, useRef, useCallback } from 'react'

/**
 * Polls `fetcher` every `intervalMs` (default 2 000 ms).
 * Returns { data, error, loading, refresh }.
 * Mirrors BackendMonitor_Buyer / BackendMonitor_Seller QThread behaviour.
 */
export function usePolling<T>(
  fetcher: () => Promise<T>,
  intervalMs = 2000,
) {
  const [data,     setData]     = useState<T | null>(null)
  const [error,    setError]    = useState<string | null>(null)
  const [loading,  setLoading]  = useState(true)
  const [hasData,  setHasData]  = useState(false)  // true once at least one successful fetch
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const refresh = useCallback(async () => {
    try {
      const result = await fetcher()
      setData(result)
      setError(null)
      setHasData(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [fetcher])

  useEffect(() => {
    refresh()
    timerRef.current = setInterval(refresh, intervalMs)
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [refresh, intervalMs])

  return { data, error, loading, refresh, hasData }
}
